import React, { useEffect } from "react";
import "./App.css";
import "./styles/Blog.css";
import { BLOG_POSTS, postPath } from "./BlogPosts";

/* ------------------------------------------------------------------
   Blog post: Should I repair or replace my appliance?
   Route: /blog/repair-or-replace-appliance
------------------------------------------------------------------- */
const POST = BLOG_POSTS.find((p) => p.slug === "repair-or-replace-appliance");
const SITE = "https://atappliancemaster.com";
const PAGE_URL = SITE + postPath(POST.slug);
const PAGE_TITLE = "Repair or Replace Your Appliance? Technician Advice | AT Appliance";

const PHONE_DISPLAY = "(331) 600-4935";
const PHONE_LINK = "tel:+13316004935";

/* FILL IN if you like — shown under the title when set */
const AUTHOR_NAME = ""; // e.g. "Ali T., owner"

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: POST.title,
    description: POST.description,
    datePublished: POST.date,
    dateModified: POST.date,
    mainEntityOfPage: PAGE_URL,
    image: SITE + "/og-image.jpg",
    author: AUTHOR_NAME
      ? { "@type": "Person", name: AUTHOR_NAME }
      : { "@type": "Organization", name: "AT Appliance Repair and Maintenance" },
    publisher: { "@type": "Organization", name: "AT Appliance Repair and Maintenance" },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: SITE + "/blog" },
      { "@type": "ListItem", position: 3, name: "Repair or Replace", item: PAGE_URL },
    ],
  },
];

export default function BlogRepairOrReplace() {
  useEffect(() => {
    document.title = PAGE_TITLE;
    const set = (selector, tag, attr, key, valueAttr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(tag);
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute(valueAttr, value);
    };
    set('meta[name="description"]', "meta", "name", "description", "content", POST.description);
    set('link[rel="canonical"]', "link", "rel", "canonical", "href", PAGE_URL);
  }, []);

  const otherPosts = BLOG_POSTS.filter((p) => p.slug !== POST.slug);

  return (
    <main className="blog-post">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SCHEMA) }} />

      <nav className="breadcrumb" aria-label="Breadcrumb">
        <a href="/">Home</a> / <a href="/blog">Blog</a> / <span>Repair or Replace</span>
      </nav>

      <article>
        <header className="post-header">
          <p className="post-kicker">{POST.category}</p>
          <h1>Should I Repair or Replace My Appliance? Advice From Appliance Repair Technicians</h1>
          <p className="post-meta">
            {AUTHOR_NAME ? `By ${AUTHOR_NAME} · ` : "AT Appliance Repair and Maintenance · "}
            <time dateTime={POST.date}>{POST.dateLabel}</time> · {POST.readTime}
          </p>
          <p className="post-lead">
            When an appliance stops working, the first question most homeowners ask is whether it's worth fixing
            or time to buy a new one. We hear it on almost every service call. The honest answer is that most
            appliances are fixable, but not every repair makes sense. Here's how our technicians decide, plus real
            examples from our work in Plainfield, Naperville, Bolingbrook and nearby towns.
          </p>
        </header>

        <table className="example-table glance-table">
          <caption>Repair or replace at a glance</caption>
          <thead>
            <tr>
              <th scope="col">Your appliance</th>
              <th scope="col">Our usual advice</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Standard appliance, 4 to 9 years old</td>
              <td>Repair</td>
            </tr>
            <tr>
              <td>Standard appliance past 10 to 12 years, with several problems</td>
              <td>Often replace</td>
            </tr>
            <tr>
              <td>High-end brand (Sub-Zero, Wolf, Thermador, Miele)</td>
              <td>Almost always repair</td>
            </tr>
          </tbody>
        </table>
        <p className="table-note">We always inspect the appliance before recommending repair or replacement.</p>

        <h2>How long do appliances usually last?</h2>
        <p>
          The age of your appliance is the first thing to consider. Most standard residential appliances last
          about 10 to 12 years. High-end brands like Sub-Zero, Wolf, Thermador and Miele are built to last much
          longer, often 15 to 25 years.
        </p>
        <p>
          Knowing where your appliance falls in its lifespan gives you a good starting point. A five-year-old
          refrigerator still has plenty of life left. A twelve-year-old washer with several problems may not.
        </p>

        <h2>Our rule of thumb: repair appliances that are 4 to 9 years old</h2>
        <p>
          If your standard appliance is between 4 and 9 years old, we usually recommend repairing it. At that age,
          a repair almost always costs less than buying a new unit, and the appliance still has years of use ahead
          of it.
        </p>
        <p>
          Age is only part of the picture, though. We don't recommend repair or replacement until we've inspected
          the appliance.
        </p>

        <h2>What our technicians look at before recommending repair or replacement</h2>
        <p>
          During a diagnostic visit, our technicians check the full condition of your appliance, not just the
          obvious problem. We look at:
        </p>
        <ul className="check-list">
          <li>The brand and model</li>
          <li>The age of the appliance and how much of its expected lifespan is left</li>
          <li>The cost of the part or parts needed</li>
          <li>The total cost of the repair</li>
          <li>The overall technical condition of the unit</li>
        </ul>
        <p>
          Sometimes an appliance has more than one problem. When that happens, we tell you exactly what needs to be
          repaired so you can make an informed decision. If you agree, we order the necessary parts. If it's a
          simple issue, our technicians can often fix it during the first visit.
        </p>

        <h2>Real examples: when we recommended repair and when we didn't</h2>
        <div className="problem-list">
          <div className="problem">
            <h3>A refrigerator repair that saved our client close to $2,000</h3>
            <p>
              One of our clients had a refrigerator with a bad fan and a faulty water valve. They were ready to buy
              a new refrigerator, which would have cost close to $2,000. After the inspection, we replaced the fan
              and the water valve, and the refrigerator was working again. The repair saved our client money and
              the time it takes to shop for, deliver and install a new unit.
            </p>
          </div>
          <div className="problem problem-replace">
            <h3>A washer that was better off replaced</h3>
            <p>
              Another client had a washer that was more than 11 years old. Because of its age and years of use, it
              had several problems at once, not just one failed part. In that situation, repairing it wasn't the
              best use of our client's money, so we recommended replacing it with a new washer.
            </p>
          </div>
        </div>
        <p>We'd rather give you an honest answer than sell you a repair that doesn't make sense.</p>

        <h2>High-end appliances are almost always worth repairing</h2>
        <p>
          If you own a high-end appliance like a <a href="/brands/sub-zero-repair">Sub-Zero refrigerator</a>,{" "}
          <a href="/brands/wolf-repair">Wolf range</a>, <a href="/brands/thermador-repair">Thermador oven</a> or{" "}
          <a href="/brands/miele-repair">Miele dishwasher</a>, our advice is different. These brands have a much
          longer lifespan, often 15 to 25 years, so in most cases we recommend repairing them, even when they're
          older than a standard appliance would be.
        </p>
        <p>
          Replacing a high-end built-in appliance is also a much bigger investment than replacing a standard one,
          which makes repair the smarter choice for most of these units.
        </p>

        <h2>The hidden costs of replacing an appliance</h2>
        <p>
          One of the most common mistakes we see is homeowners throwing out an appliance that could have been
          fixed. Many people compare the repair price to the price tag on a new appliance, but buying new usually
          costs more than the sticker price. Replacing an appliance often includes:
        </p>
        <ul>
          <li>Hauling away your old unit</li>
          <li>Delivering the new unit to your home</li>
          <li>Installing the new appliance</li>
        </ul>
        <p>
          These costs add up, and many homeowners don't think about them until after they've decided to replace.
          That's why we tell our clients that in many cases it's worth repairing the appliance you have rather than
          buying a new one every few years.
        </p>

        <h2>When replacing your appliance makes more sense</h2>
        <p>Repair isn't always the right answer. Replacement may be the better choice when:</p>
        <ul>
          <li>Your standard appliance is past its 10 to 12 year lifespan</li>
          <li>The appliance has several problems at once because of age and use</li>
          <li>The overall condition of the unit means more repairs are likely soon</li>
        </ul>
        <p>Our technicians will tell you honestly when an appliance has reached that point.</p>

        <h2>What to expect from our diagnostic visit</h2>
        <p>
          The best way to know whether to repair or replace is to have a technician inspect your appliance. Our
          diagnostic visit is $79 for residential appliances and $99 for commercial and high-end appliances.
          During the visit, we find the problem and explain what needs to be repaired before any parts are ordered.
        </p>
        <p>Every residential repair includes a 30-day warranty on parts and labor.</p>
        <p>
          When a repair needs a special-order part, our{" "}
          <a href="/blog/50-50-payment-appliance-repair">50/50 payment plan</a> lets you pay half when you approve
          the repair and the other half once your appliance is fixed and working.
        </p>

        <aside className="post-cta">
          <h2>Not sure whether to repair or replace?</h2>
          <p>
            We repair <a href="/services/refrigerator-repair">refrigerators</a>,{" "}
            <a href="/services/washer-repair">washers</a>, <a href="/services/dryer-repair">dryers</a>,{" "}
            <a href="/services/dishwasher-repair">dishwashers</a>, <a href="/services/oven-repair">ovens</a> and{" "}
            <a href="/services/stove-repair">ranges</a> in{" "}
            <a href="/illinois-service-area/appliance-repair-plainfield-illinois">Plainfield</a>,{" "}
            <a href="/illinois-service-area/appliance-repair-naperville-illinois">Naperville</a>,{" "}
            <a href="/illinois-service-area/appliance-repair-bolingbrook-illinois">Bolingbrook</a> and{" "}
            <a href="/serviceareas">nearby towns</a>. Same-day service is available in most cases. Call us to
            schedule service, and we'll help you decide whether your appliance is worth repairing.
          </p>
          <p>
            <a className="schedule-button" href={PHONE_LINK}>Call {PHONE_DISPLAY}</a>{" "}
            <a className="schedule-button cta-secondary" href="/contact">Schedule Service</a>
          </p>
        </aside>

        {otherPosts.length > 0 && (
          <>
            <h2 className="other-brands-title">More from our blog</h2>
            <ul className="other-brands">
              {otherPosts.map((p) => (
                <li key={p.slug}><a href={postPath(p.slug)}>{p.title}</a></li>
              ))}
            </ul>
          </>
        )}
      </article>

      <a className="mobile-call" href={PHONE_LINK}>Call {PHONE_DISPLAY}</a>
    </main>
  );
}